'use client';

export type AxisId = 'make' | 'model' | 'gen';

interface FeedbackPillProps {
  label: string;
  axis: AxisId;
  matched: boolean;
  attempted: boolean;
  value?: string;
  points?: number;
}

const AXIS_TINT: Record<
  AxisId,
  { main: string; soft: string; deep: string; ring: string }
> = {
  make: {
    main: 'var(--axis-make)',
    soft: 'var(--axis-make-soft)',
    deep: 'var(--axis-make-deep)',
    ring: 'rgba(79, 138, 92, 0.28)',
  },
  model: {
    main: 'var(--axis-model)',
    soft: 'var(--axis-model-soft)',
    deep: 'var(--axis-model-deep)',
    ring: 'rgba(19, 81, 216, 0.26)',
  },
  gen: {
    main: 'var(--axis-gen)',
    soft: 'var(--axis-gen-soft)',
    deep: 'var(--axis-gen-deep)',
    ring: 'rgba(123, 79, 156, 0.26)',
  },
};

function Dot({
  filled,
  isMiss,
  tint,
  delayMs,
  animate,
}: {
  filled: boolean;
  isMiss: boolean;
  tint: (typeof AXIS_TINT)[AxisId];
  delayMs: number;
  animate: boolean;
}) {
  const background = filled
    ? tint.main
    : isMiss
      ? 'var(--fg-4)'
      : 'transparent';
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        background,
        border: filled ? 0 : '1px solid var(--border)',
        boxShadow: filled ? `0 0 0 2px ${tint.ring}` : 'none',
        animation:
          animate && filled
            ? `dotPop 300ms ease-out ${delayMs}ms both`
            : undefined,
      }}
    />
  );
}

export function FeedbackPill({
  label,
  axis,
  matched,
  attempted,
  value,
  points = 0,
}: FeedbackPillProps) {
  const tint = AXIS_TINT[axis];
  const isMiss = attempted && !matched;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        padding: '6px 8px',
        borderRadius: 6,
        background: matched ? tint.soft : 'transparent',
        minWidth: 0,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 10,
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: matched ? tint.deep : 'var(--fg-3)',
        }}
      >
        {label}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <span style={{ display: 'inline-flex', gap: 3, flexShrink: 0 }}>
          <Dot
            filled={matched && points >= 1}
            isMiss={isMiss}
            tint={tint}
            delayMs={200}
            animate={matched}
          />
          <Dot
            filled={matched && points >= 2}
            isMiss={isMiss}
            tint={tint}
            delayMs={300}
            animate={matched}
          />
          <Dot
            filled={matched && points >= 3}
            isMiss={isMiss}
            tint={tint}
            delayMs={400}
            animate={matched}
          />
        </span>
        {matched && value && (
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 12,
              fontWeight: 500,
              color: 'var(--fg-1)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
              animation: 'slideReveal 300ms ease-out 0ms both',
            }}
          >
            {value}
          </span>
        )}
        {isMiss && (
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 11,
              color: 'var(--fg-4)',
            }}
          >
            —
          </span>
        )}
      </div>
    </div>
  );
}
